import React, { useEffect, useState, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Tooltip,
  Snackbar,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Visibility as ViewIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  Campaign as CampaignIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import axios from 'axios';
import { API_URL } from '../config';
import GoogleMCPNavigation from '../components/GoogleMCPNavigation';

interface Campaign {
  id: number;
  campaign_id: string;
  campaign_name: string;
  campaign_type: string;
  status: string;
  budget_amount: number;
  target_keywords: string[];
  target_locations: string[];
  start_date: string;
  end_date: string;
  created_at: string;
  metrics?: {
    impressions: number;
    clicks: number;
    cost: number;
    conversions: number;
    ctr: number;
    cpc: number;
  };
}

interface CampaignFormData {
  campaign_name: string;
  campaign_type: string;
  budget_amount: number;
  target_keywords: string[];
  target_locations: string[];
  start_date: string;
  end_date: string;
}

const GoogleMCPCampaigns: React.FC = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
  const [formData, setFormData] = useState<CampaignFormData>({
    campaign_name: '',
    campaign_type: 'search',
    budget_amount: 100,
    target_keywords: [],
    target_locations: ['Brasil'],
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning',
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [keywordInput, setKeywordInput] = useState('');

  const campaignTypes = [
    { value: 'search', label: 'Pesquisa' },
    { value: 'display', label: 'Display' },
    { value: 'shopping', label: 'Shopping' },
    { value: 'video', label: 'Vídeo' }
  ];

  const defaultKeywords = [
    'consórcio', 'consórcio de carros', 'consórcio de motos', 'consórcio imobiliário',
    'carta de crédito', 'financiamento consórcio', 'consórcio contemplado',
    'como funciona consórcio', 'consórcio vale a pena', 'lance consórcio'
  ];

  const locations = [
    'Brasil', 'São Paulo', 'Rio de Janeiro', 'Minas Gerais', 'Paraná',
    'Santa Catarina', 'Rio Grande do Sul', 'Bahia', 'Goiás', 'Distrito Federal'
  ];

  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/google-mcp/campaigns`);
      setCampaigns(response.data.data || []);
    } catch (error: any) {
      console.error('Erro ao carregar campanhas:', error);
      showSnackbar('Erro ao carregar campanhas', 'error');
      // Dados simulados para desenvolvimento
      setCampaigns([
        {
          id: 1,
          campaign_id: 'camp_001',
          campaign_name: 'Consórcio Carros - Geral',
          campaign_type: 'search',
          status: 'active',
          budget_amount: 500,
          target_keywords: ['consórcio carros', 'financiamento carro', 'carta de crédito'],
          target_locations: ['Brasil', 'São Paulo'],
          start_date: '2024-01-01',
          end_date: '2024-12-31',
          created_at: '2024-01-01T00:00:00Z',
          metrics: {
            impressions: 15420,
            clicks: 487,
            cost: 1250.75,
            conversions: 23,
            ctr: 3.16,
            cpc: 2.57
          }
        },
        {
          id: 2,
          campaign_id: 'camp_002',
          campaign_name: 'Consórcio Imóveis - Premium',
          campaign_type: 'search',
          status: 'paused',
          budget_amount: 800,
          target_keywords: ['consórcio imóveis', 'consórcio casa', 'financiamento imóvel'],
          target_locations: ['São Paulo', 'Rio de Janeiro'],
          start_date: '2024-02-01',
          end_date: '2024-12-31',
          created_at: '2024-02-01T00:00:00Z',
          metrics: {
            impressions: 8930,
            clicks: 234,
            cost: 890.50,
            conversions: 12,
            ctr: 2.62,
            cpc: 3.81
          }
        }
      ]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  const handleOpenModal = (campaignToEdit?: Campaign) => {
    if (campaignToEdit) {
      setEditingCampaign(campaignToEdit);
      setFormData({
        campaign_name: campaignToEdit.campaign_name,
        campaign_type: campaignToEdit.campaign_type,
        budget_amount: campaignToEdit.budget_amount,
        target_keywords: campaignToEdit.target_keywords,
        target_locations: campaignToEdit.target_locations,
        start_date: campaignToEdit.start_date.split('T')[0],
        end_date: campaignToEdit.end_date.split('T')[0]
      });
    } else {
      setEditingCampaign(null);
      setFormData({
        campaign_name: '',
        campaign_type: 'search',
        budget_amount: 100,
        target_keywords: [],
        target_locations: ['Brasil'],
        start_date: new Date().toISOString().split('T')[0],
        end_date: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
      });
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingCampaign(null);
    setKeywordInput('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingCampaign) {
        await axios.put(`${API_URL}/api/google-mcp/campaigns/${editingCampaign.id}`, formData);
        showSnackbar('Campanha atualizada com sucesso!', 'success');
      } else {
        await axios.post(`${API_URL}/api/google-mcp/campaigns`, formData);
        showSnackbar('Campanha criada com sucesso!', 'success');
      }
      
      handleCloseModal();
      fetchCampaigns();
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Erro ao salvar campanha';
      showSnackbar(message, 'error');
    }
    setLoading(false);
  };

  const toggleCampaignStatus = async (campaign: Campaign) => {
    try {
      const newStatus = campaign.status === 'active' ? 'paused' : 'active';
      await axios.patch(`${API_URL}/api/google-mcp/campaigns/${campaign.id}/status`, {
        status: newStatus
      });
      showSnackbar(`Campanha ${newStatus === 'active' ? 'ativada' : 'pausada'} com sucesso!`, 'success');
      fetchCampaigns();
    } catch (error: any) {
      showSnackbar('Erro ao alterar status da campanha', 'error');
    }
  };

  const addKeyword = () => {
    if (keywordInput.trim() && !formData.target_keywords.includes(keywordInput.trim())) {
      setFormData({
        ...formData,
        target_keywords: [...formData.target_keywords, keywordInput.trim()]
      });
      setKeywordInput('');
    }
  };

  const removeKeyword = (keyword: string) => {
    setFormData({
      ...formData,
      target_keywords: formData.target_keywords.filter(k => k !== keyword)
    });
  };

  const addDefaultKeywords = () => {
    const newKeywords = defaultKeywords.filter(k => !formData.target_keywords.includes(k));
    setFormData({
      ...formData,
      target_keywords: [...formData.target_keywords, ...newKeywords]
    });
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({ open: true, message, severity });
  };

  const getStatusChip = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return <Chip label="Ativa" color="success" size="small" />;
      case 'paused':
        return <Chip label="Pausada" color="warning" size="small" />;
      case 'ended':
        return <Chip label="Finalizada" color="default" size="small" />;
      default:
        return <Chip label={status} color="default" size="small" />;
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const filteredCampaigns = campaigns.filter(campaign => {
    const matchesSearch = campaign.campaign_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || campaign.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <Container maxWidth="lg">
      {/* Navegação */}
      <GoogleMCPNavigation currentPage="campaigns" />
      
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Campanhas Google MCP
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenModal()}
          >
            Nova Campanha
          </Button>
        </Box>
        <Typography variant="subtitle1" color="text.secondary">
          Gerencie suas campanhas de captação de leads no Google Ads
        </Typography>
      </Box>

      {/* Filtros */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Buscar campanhas..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="active">Ativas</MenuItem>
                <MenuItem value="paused">Pausadas</MenuItem>
                <MenuItem value="ended">Finalizadas</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" color="text.secondary">
              {filteredCampaigns.length} campanha(s) encontrada(s)
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Resumo das Campanhas */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CampaignIcon color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">{campaigns.length}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total de Campanhas
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PlayIcon color="success" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">
                    {campaigns.filter(c => c.status === 'active').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Campanhas Ativas
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TrendingUpIcon color="info" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">
                    {campaigns.reduce((sum, c) => sum + (c.metrics?.impressions || 0), 0).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Impressões
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <MoneyIcon color="warning" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">
                    {formatCurrency(campaigns.reduce((sum, c) => sum + (c.metrics?.cost || 0), 0))}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Custo Total
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabela de Campanhas */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nome da Campanha</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Orçamento</TableCell>
              <TableCell>Impressões</TableCell>
              <TableCell>Cliques</TableCell>
              <TableCell>CTR</TableCell>
              <TableCell>Custo</TableCell>
              <TableCell>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && campaigns.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : filteredCampaigns.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography variant="body2" color="text.secondary">
                    Nenhuma campanha encontrada
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredCampaigns.map((campaign) => (
                <TableRow key={campaign.id} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="body1" fontWeight="medium">
                        {campaign.campaign_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        ID: {campaign.campaign_id}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={campaignTypes.find(t => t.value === campaign.campaign_type)?.label || campaign.campaign_type}
                      variant="outlined"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{getStatusChip(campaign.status)}</TableCell>
                  <TableCell>{formatCurrency(campaign.budget_amount)}</TableCell>
                  <TableCell>{campaign.metrics?.impressions?.toLocaleString() || '-'}</TableCell>
                  <TableCell>{campaign.metrics?.clicks?.toLocaleString() || '-'}</TableCell>
                  <TableCell>{campaign.metrics?.ctr ? `${campaign.metrics.ctr}%` : '-'}</TableCell>
                  <TableCell>{campaign.metrics?.cost ? formatCurrency(campaign.metrics.cost) : '-'}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title={campaign.status === 'active' ? 'Pausar' : 'Ativar'}>
                        <IconButton
                          size="small"
                          onClick={() => toggleCampaignStatus(campaign)}
                          color={campaign.status === 'active' ? 'warning' : 'success'}
                        >
                          {campaign.status === 'active' ? <PauseIcon /> : <PlayIcon />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editar">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenModal(campaign)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Ver detalhes">
                        <IconButton size="small">
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Modal de Criação/Edição */}
      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            {editingCampaign ? 'Editar Campanha' : 'Nova Campanha'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Nome da Campanha"
                  value={formData.campaign_name}
                  onChange={(e) => setFormData({ ...formData, campaign_name: e.target.value })}
                  required
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Tipo de Campanha</InputLabel>
                  <Select
                    value={formData.campaign_type}
                    label="Tipo de Campanha"
                    onChange={(e) => setFormData({ ...formData, campaign_type: e.target.value })}
                  >
                    {campaignTypes.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Orçamento Diário"
                  type="number"
                  value={formData.budget_amount}
                  onChange={(e) => setFormData({ ...formData, budget_amount: parseFloat(e.target.value) })}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                  }}
                  required
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Data de Início"
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Data de Fim"
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  options={locations}
                  value={formData.target_locations}
                  onChange={(_, newValue) => setFormData({ ...formData, target_locations: newValue })}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip variant="outlined" label={option} {...getTagProps({ index })} />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Localizações Alvo"
                      placeholder="Selecione as localizações"
                    />
                  )}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Palavras-chave Alvo
                </Typography>
                
                <Box sx={{ mb: 2 }}>
                  <TextField
                    fullWidth
                    label="Adicionar palavra-chave"
                    value={keywordInput}
                    onChange={(e) => setKeywordInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addKeyword();
                      }
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Button onClick={addKeyword} size="small">
                            Adicionar
                          </Button>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
                
                <Box sx={{ mb: 2 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={addDefaultKeywords}
                    disabled={defaultKeywords.every(k => formData.target_keywords.includes(k))}
                  >
                    Adicionar Palavras-chave Padrão de Consórcio
                  </Button>
                </Box>
                
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {formData.target_keywords.map((keyword, index) => (
                    <Chip
                      key={index}
                      label={keyword}
                      onDelete={() => removeKeyword(keyword)}
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>
                
                {formData.target_keywords.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Adicione pelo menos uma palavra-chave para a campanha
                  </Typography>
                )}
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseModal}>Cancelar</Button>
            <Button 
              type="submit" 
              variant="contained"
              disabled={loading || formData.target_keywords.length === 0}
            >
              {loading ? 'Salvando...' : (editingCampaign ? 'Atualizar' : 'Criar')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default GoogleMCPCampaigns;